import type { ReactNode } from 'react';
import type { PublicPlan } from '@neuroedge/shared';
import { PageState } from '../../components/ui';
import { PlanCard, PlanGrid } from '../../components/billing/PlanCard';
import { usePublicPlans } from '../../api/billing';
import { contactEmailHref } from '../../marketing/contact';

/**
 * The plans on the pricing page (GET /public/plans), as cards. Shared by the
 * public /pricing page and the in-app Plans menu; \`actionFor\` renders each
 * card's call to action.
 */
export function PlansCatalog({
  actionFor,
  highlightCode,
}: {
  actionFor: (plan: PublicPlan) => ReactNode;
  /** Mark this plan (e.g. the workspace's current one). */
  highlightCode?: string | null;
}) {
  const plans = usePublicPlans();
  if (plans.isPending) return <PageState kind="loading" message="Loading plans…" />;
  if (plans.isError) {
    return (
      <PageState kind="error" message={plans.error.message} onRetry={() => void plans.refetch()} />
    );
  }
  if (plans.data.length === 0) return <PageState kind="empty" title="No plans to show yet" />;
  return (
    <PlanGrid>
      {plans.data.map((p) => (
        <PlanCard key={p.code} plan={p} highlighted={p.code === highlightCode}>
          {actionFor(p)}
        </PlanCard>
      ))}
    </PlanGrid>
  );
}

/** "Contact us" for custom-priced plans (Enterprise). */
export function ContactSales({ plan }: { plan: PublicPlan }) {
  return (
    <a className="ne-btn ne-btn--secondary" href={contactEmailHref(`${plan.name} plan`)}>
      Contact us
    </a>
  );
}
